import { datadogRum } from '@datadog/browser-rum';
import { isEmbedded } from '@commander4/elements/lib/embed-protocol';

/**
 * Initialize Datadog RUM (Real User Monitoring)
 * Monitors frontend performance, errors, and user sessions
 *
 * Uses shared P7S1 Datadog application across all joyia services.
 * Service differentiation happens via the 'service' tag.
 */
export function initDatadog() {
  const applicationId = import.meta.env.VITE_DATADOG_RUM_APPLICATION_ID;
  const clientToken = import.meta.env.VITE_DATADOG_RUM_CLIENT_TOKEN;
  const env = import.meta.env.VITE_DATADOG_ENV || 'dev';
  // UST version, supplied by infra alongside env. Without it every RUM session reported no
  // version, so a regression could not be tied to a deploy.
  const version = import.meta.env.VITE_DATADOG_VERSION;

  // Skip Datadog in local development (dev server running on localhost)
  if (typeof window !== 'undefined' && (
    window.location.hostname === 'localhost' ||
    window.location.hostname === '127.0.0.1'
  )) {
    console.log('Datadog RUM disabled in local development');
    return;
  }

  // Only initialize if credentials are available (controlled by infra/chat.ts)
  if (!applicationId || !clientToken) {
    console.log('Datadog RUM disabled (no credentials provided)');
    return;
  }

  try {
    datadogRum.init({
      applicationId,
      clientToken,
      site: 'datadoghq.eu',
      service: 'joyia-file-viewer', // Service tag for filtering in Datadog
      env,
      ...(version ? { version } : {}),
      sessionSampleRate: 100,
      /*
       * No session replay when embedded — Joyia Web's shell is already recording.
       *
       * Each embedded app initialising its OWN replay recorder serialises every DOM
       * mutation it sees, and the cost is a BLOCKED MAIN THREAD, not network: the symptom
       * is a stack of `rum` fetches stuck pending with no status, and a frame that renders
       * white. Only the host's recording is ever watched, and it already captures these
       * frames visually, so the extra recorder buys nothing.
       *
       * This app needed the guard the moment it became an embedded app rather than a bare
       * legacy iframe — it is the most code-split frontend on the platform, so it is also
       * the worst one to record twice. Error and performance telemetry is untouched;
       * opened standalone in its own tab it still records normally.
       *
       * `isEmbedded()` is safe at module scope: it reads the `?embedded=1` param the host
       * always appends before falling back to a frame check.
       */
      sessionReplaySampleRate: isEmbedded() ? 0 : 20,
      trackBfcacheViews: true,
      defaultPrivacyLevel: 'mask-user-input',
    });

    console.log('Datadog RUM initialized for joyia-file-viewer');
  } catch (error) {
    console.error('Failed to initialize Datadog RUM:', error);
  }
}
